/**
 * Org-as-code workspace — the org dir for the active layout (`wayai-ws/org/`,
 * or the legacy root-level `org/` for un-migrated repos; see `layout.ts`).
 *
 *   <orgDir>/
 *     resources.yaml        # resource metadata (files stripped, like hub.yaml)
 *     resources/<slug>/...   # file trees (SKILL.md, docs/…, binaries)
 *
 * Sits beside the hub folders under the same `wayai-ws/` root. One org per repo
 * (org id comes from the workspace manifest `wayai-ws/wayai.yaml`, or the
 * deprecated root `.wayai.yaml`). Reuses the shared resource file-tree
 * primitives so binary/hash/orphan rules match hub-as-code exactly.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { slugify } from './utils.js';
import { resolveLayout } from './layout.js';
import { readRealFileOrThrow, requireRealSubdirNoSymlink, writeFileNoFollow } from './fs-safety.js';
import { scanResourceFiles, writeResourceFileTree, downloadBinaryFiles } from './resource-files.js';
import { YAML_DUMP_OPTIONS } from './yaml-writer.js';
import type { OrgResourcesPayload, OrgAsCodeResource } from './types.js';

export const ORG_MANIFEST_NAME = 'resources.yaml';

export function getOrgDir(gitRoot: string): string {
  return resolveLayout(gitRoot).orgDir;
}

export function orgManifestExists(orgDir: string): boolean {
  return fs.existsSync(path.join(orgDir, ORG_MANIFEST_NAME));
}

/** Read `org/resources.yaml` + scan `org/resources/<slug>/` into a payload. */
export function parseOrgResources(orgDir: string): OrgResourcesPayload {
  const resourcesDir = path.join(orgDir, 'resources');
  // Everything read here is uploaded by `org push`/`org diff`.
  requireRealSubdirNoSymlink(orgDir, resourcesDir, false);
  const manifestText = readRealFileOrThrow(orgDir, path.join(orgDir, ORG_MANIFEST_NAME));
  const manifest = ((manifestText !== null ? yaml.load(manifestText) : null) ?? {}) as Record<string, unknown>;
  const rawResources = Array.isArray(manifest.resources)
    ? (manifest.resources as Array<Record<string, unknown>>)
    : [];

  const resources: OrgAsCodeResource[] = rawResources.map((res) => {
    const resource: OrgAsCodeResource = { name: res.name as string };
    if (res.id) resource.id = res.id as string;
    if (res.type) resource.type = res.type as string;
    if (res.description) resource.description = res.description as string;
    if (res.enabled === false) resource.enabled = false;
    if (res.skill_name) resource.skill_name = res.skill_name as string;
    if (res.environment) resource.environment = res.environment as OrgAsCodeResource['environment'];
    if (Array.isArray(res.tags)) resource.tags = res.tags as string[];
    if (Array.isArray(res.folders)) resource.folders = res.folders as OrgAsCodeResource['folders'];

    const resDir = path.join(resourcesDir, slugify(resource.name));
    requireRealSubdirNoSymlink(orgDir, resDir, false);
    if (fs.existsSync(resDir)) {
      const files = scanResourceFiles(resDir, '');
      if (files.length > 0) resource.files = files;
    }
    return resource;
  });

  return { version: 1, resources };
}

/** Write `resources.yaml` (files stripped) + file trees + orphan-dir cleanup. */
export function writeOrgResources(orgDir: string, payload: OrgResourcesPayload): void {
  fs.mkdirSync(orgDir, { recursive: true });
  const resourcesDir = path.join(orgDir, 'resources');
  // Before any write: the orphan prune below lists resources/ and recursively removes
  // what it finds, so a symlinked resources/ would delete folders at the link target.
  requireRealSubdirNoSymlink(orgDir, resourcesDir, false);
  const resources = payload.resources ?? [];

  // Manifest carries metadata + folders; file content lives on disk.
  const manifestResources = resources.map((r) => {
    const { files: _files, ...rest } = r;
    return rest;
  });
  writeFileNoFollow(
    orgDir,
    path.join(orgDir, ORG_MANIFEST_NAME),
    Buffer.from(yaml.dump({ version: 1, resources: manifestResources }, YAML_DUMP_OPTIONS), 'utf-8'),
  );

  const currentSlugs = new Set<string>();
  for (const resource of resources) {
    const resSlug = slugify(resource.name);
    currentSlugs.add(resSlug);
    writeResourceFileTree(path.join(resourcesDir, resSlug), resource.files || [], orgDir);
  }

  // Remove orphan resource directories.
  if (fs.existsSync(resourcesDir)) {
    for (const entry of fs.readdirSync(resourcesDir, { withFileTypes: true })) {
      if (entry.isDirectory() && !currentSlugs.has(entry.name)) {
        fs.rmSync(path.join(resourcesDir, entry.name), { recursive: true, force: true });
      }
    }
  }
}

/** Download binary files (download_url, no inline content) into `org/resources/<slug>/`. */
export async function downloadOrgBinaryFiles(orgDir: string, payload: OrgResourcesPayload): Promise<void> {
  let count = 0;
  for (const resource of payload.resources ?? []) {
    if (!resource.files) continue;
    const resDir = path.join(orgDir, 'resources', slugify(resource.name));
    count += await downloadBinaryFiles(resDir, resource.files, orgDir);
  }
  if (count > 0) console.log(`Downloaded ${count} binary resource file(s).`);
}
